<template>
  <ProfessionDetailSkeleton v-if="props.loading" />

  <div
    v-else-if="props.professionData"
    class="profession-detail">
    <!-- ── HERO BANNER ─────────────────────────────────────────────── -->
    <section class="hero-banner">
      <img
        :src="coverHeroSrc"
        alt="Métier PrePeers"
        class="hero-banner__bg-image" />
      <img
        :src="coverHeroSrc"
        alt="Métier PrePeers"
        class="hero-banner__image" />
    </section>

    <PrepeersBreadcrumb
      auto
      section="metiers"
      :current-label="props.professionData?.professiondatas?.h1" />

    <!-- ── HERO + H1 (sans timeReading) ─────────────────────────────── -->
    <section class="hero-section">
      <div class="container">
        <h1 class="hero-section__h1">{{ props.professionData?.professiondatas?.h1 }}</h1>
        <div
          class="hero-section__intro"
          v-html="formatSeoIntro(props.professionData?.professiondatas?.introduction ?? '')" />
        <div class="hero-section__line" />
      </div>
    </section>

    <!-- ── TITRE VIDÉO + VIDEOS (0 à 3) ─────────────────────────────── -->
    <UiPrePeersVideoGrid
      :title="videoSectionTitle"
      :video-urls="videoUrls" />

    <!-- ── INFOS CLÉS (Metrics) — même bloc que "Présentation en chiffres" école ── -->
    <UiPrePeersMetricsGrid
      :block="props.professionData?.parsedMetricsBlock"
      :max-columns="3"
      relaxed-vertical-spacing
      compact-value-desktop />

    <!-- ── CENTRES D'INTÉRÊT ────────────────────────────────────────── -->
    <section
      v-if="props.professionData?.parsedInterestsBlock?.Links?.length"
      class="level-nav-section">
      <div class="container">
        <h2 class="level-nav__title">{{ props.professionData?.parsedInterestsBlock.Title }}</h2>
        <div class="level-nav__list">
          <PrePeersNavLink
            v-for="sl in props.professionData?.parsedInterestsBlock.Links"
            :key="sl.Url"
            :to="localePath(sl.Url)"
            >{{ sl.Text }}</PrePeersNavLink
          >
        </div>
      </div>
    </section>

    <!-- ── CTA SI NON CONNECTÉ ──────────────────────────────────────── -->
    <section
      v-if="!props.isLoggedIn"
      class="cta-section">
      <div class="container">
        <PrePeersLolaCta
          variant="metier"
          :entity-name="professionName" />
      </div>
    </section>

    <!-- ── CONTENU HTML RICHE ───────────────────────────────────────── -->
    <section
      v-if="props.professionData?.professiondatas.contentText"
      class="content-section">
      <div class="container">
        <UiPrepeersQlEditorContent
          :html="props.professionData?.professiondatas.contentText ?? ''" />
      </div>
    </section>

    <!-- ── QUELLES ÉTUDES (formations) ─────────────────────────────── -->
    <section
      v-if="props.professionData?.parsedPartners?.length"
      class="media-grid-section">
      <div class="container">
        <h2 class="media-grid__title">{{ studiesTitle }}</h2>
        <p class="media-grid__desc">{{ t('seointer.profession.description_studies') }}</p>
        <div class="media-grid__cards">
          <UiProgramCard
            v-for="program in props.professionData?.parsedPartners"
            :key="program.Id"
            :program="program" />
        </div>
      </div>
    </section>

    <!-- ── MÉTIERS LIÉS (ProfessionCard) ────────────────────────────── -->
    <section
      v-if="props.professionData?.parsedRelatedProfessions?.length"
      class="media-grid-section">
      <div class="container">
        <h2 class="media-grid__title">{{ relatedTitle }}</h2>
        <p class="media-grid__desc">{{ t('seointer.profession.description_related') }}</p>
        <div class="media-grid__cards">
          <UiProfessionCard
            v-for="profession in props.professionData?.parsedRelatedProfessions"
            :key="profession.id"
            :profession="profession" />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef, PropType } from 'vue'
import PrePeersNavLink from '~/components/core/PrePeersNavLink.vue'
import ProfessionDetailSkeleton from '~/components/metiers/ProfessionDetailSkeleton.vue'
import PrepeersBreadcrumb from '~/components/navigation/PrepeersBreadcrumb.vue'
import type { ProfessionDataResponse } from '~/types/profession'
import PrePeersLolaCta from '~/components/ui/PrePeersLolaCta.vue'
import { formatSeoIntro } from '~/utils/helpers'

interface ProfessionProps {
  professionData: ProfessionDataResponse | null
  loading: boolean
  isLoggedIn: boolean
}

/**
 * Définit les props du composant MetiersProfession (fiche métier détaillée).
 */
const props: ProfessionProps = defineProps({
  professionData: {
    type: Object as PropType<ProfessionDataResponse | null>,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isLoggedIn: {
    type: Boolean,
    default: false,
  },
})

const { t }: { t: (key: string, named?: Record<string, unknown>) => string } = useI18n()
const localePath: ReturnType<typeof useLocalePath> = useLocalePath()

const professionName: ComputedRef<string> = computed<string>(
  (): string => props.professionData?.professiondatas?.name ?? '',
)

/** Image de couverture bannière ou placeholder si l'API ne fournit pas d'URL. */
const coverHeroSrc: ComputedRef<string> = computed<string>((): string => {
  const pic: string | undefined | null = props.professionData?.professiondatas?.coverPict
  const s: string = typeof pic === 'string' ? pic.trim() : ''
  return s.length > 0 ? s : '/images/emptyCoverPicture.png'
})

const videoSectionTitle: ComputedRef<string> = computed<string>(
  (): string => `${professionName.value} : ${t('seointer.videoTextSuffix')}`,
)

/** Liste des URLs vidéo bornée à 3 (`v-if` masque la section si vide). */
const videoUrls: ComputedRef<string[]> = computed<string[]>((): string[] => {
  const raw: string[] | undefined = props.professionData?.professiondatas?.videoUrls
  if (!Array.isArray(raw)) return []
  return raw
    .map((u: string): string => (u ?? '').trim())
    .filter((u: string): boolean => u.length > 0)
    .slice(0, 3)
})

const studiesTitle: ComputedRef<string> = computed<string>((): string =>
  t('seointer.profession.title_studies', { name: professionName.value }),
)

const relatedTitle: ComputedRef<string> = computed<string>((): string =>
  t('seointer.profession.title_related', { name: professionName.value }),
)
</script>
